import React, { useState, useEffect } from 'react';
import { Layout } from 'antd';
import { motion, AnimatePresence } from 'framer-motion';
import { Outlet, useLocation } from 'react-router-dom';
import { MenuOutlined } from '@ant-design/icons';
import Sidebar from './Sidebar';

const { Content } = Layout;

const MainLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(true); // Default to collapsed
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth <= 768) {
        setCollapsed(true); // Always collapse on mobile
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const pageVariants = {
    initial: {
      opacity: 0,
      x: 20
    },
    in: {
      opacity: 1,
      x: 0
    },
    out: {
      opacity: 0,
      x: -20
    }
  };

  const pageTransition = {
    duration: 0.3
  };


  return (
    <Layout style={{ minHeight: '100vh', background: '#262624', position: 'relative', overflow: 'hidden' }}>
      {/* Multi-color gradient ripples background */}
      <motion.div
        style={{
          position: 'absolute',
          top: '10%',
          left: '5%',
          width: '500px',
          height: '500px',
          background: 'radial-gradient(circle, rgba(147, 51, 234, 0.15) 0%, rgba(168, 85, 247, 0.1) 35%, transparent 65%)',
          borderRadius: '50%',
          filter: 'blur(80px)',
          pointerEvents: 'none',
          zIndex: 0
        }}
        animate={{
          x: [0, 80, 0],
          y: [0, 60, 0],
          scale: [1, 1.3, 1]
        }}
        transition={{
          duration: 12,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      <motion.div
        style={{
          position: 'absolute',
          top: '40%',
          right: '10%',
          width: '450px',
          height: '450px',
          background: 'radial-gradient(circle, rgba(59, 130, 246, 0.18) 0%, rgba(96, 165, 250, 0.12) 35%, transparent 65%)',
          borderRadius: '50%',
          filter: 'blur(90px)',
          pointerEvents: 'none',
          zIndex: 0
        }}
        animate={{
          x: [0, -70, 0],
          y: [0, 50, 0],
          scale: [1, 1.35, 1]
        }}
        transition={{
          duration: 14,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1
        }}
      />
      <motion.div
        style={{
          position: 'absolute',
          bottom: '20%',
          left: '30%',
          width: '400px',
          height: '400px',
          background: 'radial-gradient(circle, rgba(6, 182, 212, 0.15) 0%, rgba(34, 211, 238, 0.1) 35%, transparent 65%)',
          borderRadius: '50%',
          filter: 'blur(85px)',
          pointerEvents: 'none',
          zIndex: 0
        }}
        animate={{
          x: [0, -60, 0],
          y: [0, -40, 0],
          scale: [1, 1.28, 1]
        }}
        transition={{
          duration: 13,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 2
        }}
      />

      {/* Sidebar */}
      <Sidebar collapsed={collapsed} onCollapse={setCollapsed} isMobile={isMobile} />

      {/* Mobile Hamburger Menu Button */}
      {isMobile && collapsed && (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={() => setCollapsed(false)}
          style={{
            position: 'fixed',
            top: '20px',
            left: '16px',
            zIndex: 101,
            background: 'rgba(31, 33, 33, 0.9)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            borderRadius: '8px',
            width: '44px',
            height: '44px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
          }}
        >
          <MenuOutlined style={{ color: '#FFFFFF', fontSize: '20px' }} />
        </motion.button>
      )}

      {/* Main Content Area */}
      <Layout
        style={{
          marginLeft: isMobile ? '0' : (collapsed ? '60px' : '280px'), // No margin on mobile
          background: 'transparent',
          position: 'relative',
          zIndex: 1,
          transition: 'margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      >

        {/* Page Content */}
        <Content
          style={{
            padding: isMobile ? '16px' : '24px',
            paddingTop: isMobile ? '70px' : '24px', // Extra padding for mobile hamburger menu
            minHeight: '100vh',
            height: '100vh',
            background: 'transparent',
            overflow: 'hidden'
          }}
        >
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={location.pathname}
              initial="initial"
              animate="in"
              exit="out"
              variants={pageVariants}
              transition={pageTransition}
              style={{
                width: '100%',
                height: '100%'
              }}
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </Content>
      </Layout>
      
      {/* Mobile Overlay for Sidebar */}
      <AnimatePresence>
        {!collapsed && isMobile && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.7)',
              zIndex: 99,
              display: 'block'
            }}
            onClick={() => setCollapsed(true)}
          />
        )}
      </AnimatePresence>
    </Layout>
  );
};

export default MainLayout;